import React, { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'framer-motion';
import { cn } from '../../../lib/utils';

interface PixelMountainsProps {
  className?: string;
  pixelSize?: number;
  layers?: number;
  seed?: number;
  palette?: string[];
  stagger?: number;
}

type RGB = [number, number, number];

const DEFAULT_PALETTE = ['#dbe6f5', '#c2d4ee', '#9dbbe3', '#6f97cf', '#3f6cb0', '#003b82'];

const BAYER4 = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
];

const mulberry32 = (a: number) => () => {
  a |= 0;
  a = (a + 0x6d2b79f5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const hexToRgb = (hex: string): RGB => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const mix = (a: RGB, b: RGB, t: number): RGB => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];

// 1D ridged fractal value noise: octaves of smoothed lattice noise,
// folded around the midpoint so midline crossings become sharp peaks.
const makeRidge = (rand: () => number, octaves: number) => {
  const lattices = Array.from({ length: octaves }, () =>
    Array.from({ length: 256 }, rand)
  );
  const val = (lat: number[], x: number) => {
    const xi = Math.floor(x);
    const xf = x - xi;
    const a = lat[xi & 255];
    const b = lat[(xi + 1) & 255];
    const u = xf * xf * (3 - 2 * xf);
    return a + (b - a) * u;
  };
  return (x: number) => {
    let amp = 1;
    let freq = 1;
    let sum = 0;
    let norm = 0;
    for (let o = 0; o < octaves; o++) {
      sum += amp * val(lattices[o], x * freq);
      norm += amp;
      amp *= 0.5;
      freq *= 2;
    }
    const n = sum / norm;
    return 1 - Math.abs(2 * n - 1);
  };
};

// Per-layer composition (back to front): tallest dark cluster mid-back,
// paler distant ridges, lower foreground ridges. amp/baseline in units of H.
const LAYER_CONFIGS = [
  { color: 0, amp: 0.9, baseline: 1.0, freq: 4.5 },
  { color: 1, amp: 0.95, baseline: 1.02, freq: 5.0 },
  { color: 5, amp: 1.08, baseline: 1.05, freq: 5.2 },
  { color: 2, amp: 0.7, baseline: 1.06, freq: 5.8 },
  { color: 4, amp: 0.55, baseline: 1.08, freq: 6.4 },
  { color: 3, amp: 0.45, baseline: 1.1, freq: 7.0 },
];

export const PixelMountains: React.FC<PixelMountainsProps> = ({
  className,
  pixelSize = 3,
  layers = 6,
  seed = 7,
  palette = DEFAULT_PALETTE,
  stagger = 150,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const inView = useInView(wrapRef, { margin: '-40px' });

  const generate = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const cssW = wrap.clientWidth;
    const cssH = wrap.clientHeight;
    if (!cssW || !cssH) return;

    const W = Math.max(80, Math.round(cssW / pixelSize));
    const H = Math.max(40, Math.round(cssH / pixelSize));
    const rand = mulberry32(seed);
    const white: RGB = [255, 255, 255];
    const configs = LAYER_CONFIGS.slice(0, layers);

    // One canvas per depth layer so the entrance can stagger back-to-front.
    configs.forEach((cfg, idx) => {
      const canvas = canvasRefs.current[idx];
      const ridge = makeRidge(rand, 4);
      const xOff = rand() * 512;
      const freq = cfg.freq + rand() * 0.6;
      if (!canvas) return;
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = ctx.createImageData(W, H);
      const data = img.data;
      const base = hexToRgb(palette[Math.min(cfg.color, palette.length - 1)]);
      const fog = mix(base, white, 0.85);
      const amp = H * cfg.amp;
      const baseline = H * cfg.baseline;
      const fadeLen = amp * 0.55;

      for (let x = 0; x < W; x++) {
        // threshold cut: only high-noise regions rise as peaks, so clusters
        // separate with open sky between them; valleys fall below the band
        const r = ridge(xOff + (x / W) * freq);
        const n = Math.pow(Math.max(0, (r - 0.5) / 0.5), 1.35);
        const topY = Math.round(baseline - n * amp);
        for (let y = Math.max(0, topY); y < H; y++) {
          // solid color at the ridgeline, dissolving into fog further down
          const f = Math.min(1, (y - topY) / fadeLen);
          const threshold = (BAYER4[y & 3][x & 3] + 0.5) / 16;
          const c = f > threshold ? fog : base;
          const i = (y * W + x) * 4;
          data[i] = c[0];
          data[i + 1] = c[1];
          data[i + 2] = c[2];
          data[i + 3] = 255;
        }
      }

      ctx.putImageData(img, 0, 0);
    });
  }, [layers, palette, pixelSize, seed]);

  useEffect(() => {
    generate();
    const wrap = wrapRef.current;
    if (!wrap) return;
    const resizeObserver = new ResizeObserver(() => generate());
    resizeObserver.observe(wrap);
    return () => resizeObserver.disconnect();
  }, [generate]);

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      className={cn('pointer-events-none overflow-hidden select-none relative', className)}
    >
      {Array.from({ length: Math.min(layers, LAYER_CONFIGS.length) }).map((_, i, arr) => (
        <canvas
          key={i}
          ref={(el) => {
            canvasRefs.current[i] = el;
          }}
          className={cn(
            'absolute inset-0 block h-full w-full [image-rendering:pixelated]',
            'transition-all [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]',
            inView
              ? 'opacity-100 translate-y-0 duration-[1200ms]'
              : 'opacity-0 translate-y-6 duration-700 motion-reduce:translate-y-0'
          )}
          style={{
            // entering: build back-to-front; leaving: recede front-first, quicker
            transitionDelay: inView
              ? `${i * stagger}ms`
              : `${(arr.length - 1 - i) * Math.round(stagger * 0.5)}ms`,
          }}
        />
      ))}
    </div>
  );
};
